/**
 * @voltxampere/presets — Arduino blink LED topology (Sprint 50-6)
 *
 * Classic Arduino "Blink" sketch's circuit topology: ATmega328P
 * GPIO pin (PB5 / digital 13) drives an external LED through a
 * 220Ω current-limiting resistor to ground. With the GPIO modeled
 * as a 5V source (LED on phase), I_LED = (5 − 1.9V) / 220Ω ≈ 14
 * mA — matches the Sprint 17 led-current-limiter sentinel.
 *
 * Note: MVP preset uses a fixed 5V voltage source as the MCU GPIO
 * stub; full MCU simulation (`mcu-execution` schemaType + flashed
 * .hex) lands in Sprint 51 polish (Arduino blink program already
 * sentinel-locked since Sprint 47).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { makeCircuit, makeNet, makeComponent } from "../_builders.js";
export const arduinoBlinkLed = {
    id: "arduino-blink-led",
    name: { tr: "Arduino LED Blink Topolojisi", en: "Arduino LED Blink Topology" },
    category: "mcu",
    difficulty: 1,
    description: {
        tr: "Arduino 'Blink' eskizinin tipik LED+direnç bağlantısı. PB5 → 220Ω → LED → GND.",
        en: "Classic Arduino 'Blink' sketch wiring. PB5 → 220Ω → LED → GND.",
    },
    circuit: makeCircuit({
        slot: 1,
        name: "Arduino Blink LED",
        description: "Arduino GPIO + 220Ω + LED",
        nets: [
            makeNet({ name: "gnd", isGround: true }),
            makeNet({ name: "pb5" }),
            makeNet({ name: "led_anode" }),
        ],
        components: [
            makeComponent({
                id: "MCU_OUT",
                type: "voltage-source",
                params: { voltage: 5 },
                terminals: { pos: "net_pb5", neg: "net_gnd" },
                label: "PB5 (Arduino D13)",
                position: { x: -160, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "R1",
                type: "resistor",
                params: { resistance: 220 },
                terminals: { p1: "net_pb5", p2: "net_led_anode" },
                label: "R1",
                position: { x: -40, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "D1",
                type: "led",
                params: { is: 1e-12, n: 3, vForward: 1.9 },
                terminals: { anode: "net_led_anode", cathode: "net_gnd" },
                label: "D1",
                position: { x: 80, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "GND1",
                type: "ground",
                params: {},
                terminals: { p: "net_gnd" },
                position: { x: -160, y: 80 },
                rotation: 0,
            }),
        ],
        wires: [],
    }),
    expectedValues: [
        {
            node: "led_anode",
            analysis: "dc",
            value: 1.9,
            tolerance: 0.2,
            unit: "V",
        },
    ],
    learningGoals: [
        {
            tr: "Arduino GPIO pininden harici LED sürmek için akım sınırlama.",
            en: "Current limiting an external LED driven by an Arduino GPIO pin.",
        },
        {
            tr: "I_LED = (V_GPIO - V_F) / R formülüyle akım hesabı.",
            en: "Compute I_LED = (V_GPIO - V_F) / R for the LED current.",
        },
    ],
    references: [
        "Arduino Blink Tutorial (https://docs.arduino.cc/tutorials/uno-rev3/getting-started)",
    ],
};
//# sourceMappingURL=arduino-blink-led.js.map